import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Modal, Header, Grid, Segment, Icon, Button, Statistic, Divider, Message } from 'semantic-ui-react';
import { Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { DriverRanking } from '../../models/client-ranking';
import { DriverMonthPerformance } from '../../models/driver-month-performance';
import { DriverPerformanceHistoryDto } from '../../models/driver-performance-history';
import DriverPerformanceChart from './DriverPerformanceChart';
import DriverProgrammingsModal from '../programming-list-page/DriverProgrammingsModal';
import { usePerformanceHistoryService } from '../../services/backoffice/hooks/usePerformanceHistoryService';
import { Table } from 'semantic-ui-react';
import {
  formatDashboardPeriodLabel,
  resolveDashboardPeriodRange,
} from '../../shared/utils/dashboard-period';
import './DriverDetailModal.css';

ChartJS.register(ArcElement, Tooltip, Legend);

interface DriverDetailModalProps {
  open: boolean;
  onClose: () => void;
  driver: DriverRanking | null;
  /** Mesmo valor do filtro do dashboard (`currentMonth` | `7` | `30` | …). */
  period: string;
  clientAverage?: {
    inertia: number;
    motorLoad: number;
  };
}

const DriverDetailModal: React.FC<DriverDetailModalProps> = ({
  open,
  onClose,
  driver,
  period,
  clientAverage
}) => {
  const [monthPerformance, setMonthPerformance] = useState<DriverMonthPerformance | null>(null);
  const [loadingMonthData, setLoadingMonthData] = useState(false);
  const [dailyData, setDailyData] = useState<DriverPerformanceHistoryDto[]>([]);
  const [loadingDailyData, setLoadingDailyData] = useState(false);
  const [programmingsModalOpen, setProgrammingsModalOpen] = useState(false);
  const { getDriverMonthPerformance, getDriverDailyPerformance } = usePerformanceHistoryService();

  const periodLabel = useMemo(() => formatDashboardPeriodLabel(period), [period]);

  // Calcular datas do período com useMemo para evitar recálculos
  const { intervalStart, intervalEnd, currentYear, currentMonth } = useMemo(() => {
    const { inicioIntervalo, fimIntervalo } = resolveDashboardPeriodRange(period);
    return {
      intervalStart: inicioIntervalo.toISOString(),
      intervalEnd: fimIntervalo.toISOString(),
      currentYear: fimIntervalo.getFullYear(),
      currentMonth: fimIntervalo.getMonth() + 1
    };
  }, [period]);

  useEffect(() => {
    if (open && driver?.driverCpf) {
      let isMounted = true;
      
      const fetchMonthData = async () => {
        setLoadingMonthData(true);
        try {
          const data = await getDriverMonthPerformance(driver.driverCpf, currentYear, currentMonth);
          if (isMounted) {
            setMonthPerformance(data);
          }
        } catch (err) {
          console.error('Erro ao buscar dados mensais:', err);
          if (isMounted) {
            setMonthPerformance(null);
          }
        } finally {
          if (isMounted) {
            setLoadingMonthData(false);
          }
        }
      };

      const fetchDailyData = async () => {
        setLoadingDailyData(true);
        try {
          const data = await getDriverDailyPerformance(driver.driverCpf, intervalStart, intervalEnd);
          if (isMounted) {
            const sortedData = data.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
            setDailyData(sortedData);
          }
        } catch (err) {
          console.error('Erro ao buscar dados diários:', err);
          if (isMounted) {
            setDailyData([]);
          }
        } finally {
          if (isMounted) {
            setLoadingDailyData(false);
          }
        }
      };
      
      fetchMonthData();
      fetchDailyData();
      
      return () => {
        isMounted = false;
      };
    } else {
      setMonthPerformance(null);
      setDailyData([]);
      setLoadingMonthData(false);
      setLoadingDailyData(false);
    }
  }, [open, driver?.driverCpf, currentYear, currentMonth, intervalStart, intervalEnd]);

  const scrollToSection = useCallback((sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start' 
      });
    }
  }, []);

  if (!driver) return null;

  const formatCpf = (cpf: string) => {
    const cleaned = cpf.replace(/\D/g, '');
    if (cleaned.length !== 11) return cpf;
    return cleaned.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('pt-BR').format(value);
  };

  const getPerformanceLevel = (value: number, isInertia: boolean) => {
    if (isInertia) {
      // Inércia: máximo 48%, quanto maior melhor
      if (value >= 40) return { level: 'Excelente', color: 'green' };
      if (value >= 30) return { level: 'Bom', color: 'olive' };
      if (value >= 20) return { level: 'Regular', color: 'orange' };
      return { level: 'Precisa melhorar', color: 'red' };
    } else {
      // Carga do Motor: quanto menor melhor, acima de 3% já é péssimo
      if (value <= 1.5) return { level: 'Excelente', color: 'green' };
      if (value <= 2.5) return { level: 'Bom', color: 'olive' };
      if (value <= 3.0) return { level: 'Regular', color: 'orange' };
      return { level: 'Precisa melhorar', color: 'red' };
    }
  };

  const inertiaLevel = getPerformanceLevel(driver.inertiaPercentage, true);
  const motorLoadLevel = getPerformanceLevel(driver.motorLoadPercentage, false);

  const totalPrize = driver.inertiaPrize + driver.engineLoadPrize;
  
  // Dados para gráfico de pizza de premiações
  const pieData = monthPerformance ? {
    labels: ['Prêmio Inércia', 'Prêmio Carga Motor'],
    datasets: [
      {
        data: [monthPerformance.inertiaPrize || 0, monthPerformance.engineLoadPrize || 0],
        backgroundColor: [
          'rgba(34, 197, 94, 0.8)',
          'rgba(59, 130, 246, 0.8)',
        ],
        borderColor: [
          'rgb(34, 197, 94)',
          'rgb(59, 130, 246)',
        ],
        borderWidth: 2,
      },
    ],
  } : null;

  const pieOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom' as const,
        labels: {
          usePointStyle: true,
          padding: 15,
          font: {
            size: 12,
            family: "'Inter', 'Segoe UI', 'Roboto', sans-serif"
          }
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        bodyFont: {
          size: 13
        },
        callbacks: {
          label: function(context: any) {
            const label = context.label || '';
            const value = formatCurrency(context.parsed);
            const total = (monthPerformance?.inertiaPrize || 0) + (monthPerformance?.engineLoadPrize || 0);
            const percentage = total > 0 ? ((context.parsed / total) * 100).toFixed(1) : '0';
            return `${label}: ${value} (${percentage}%)`;
          }
        }
      }
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="large"
      className="driver-detail-modal"
    >
      <Modal.Header>
        <div className="modal-header-content">
          <div>
            <Icon name="user circle" size="large" />
            <span className="driver-modal-name">{driver.driverName}</span>
          </div>
          <Button icon="close" onClick={onClose} basic circular />
        </div>
      </Modal.Header>

      <Modal.Content scrolling>
        {/* Informações Básicas - sempre visível */}
        <Segment className="driver-info-segment">
          <Grid columns={4} stackable>
            <Grid.Column>
              <div className="info-item">
                <Icon name="id card" />
                <div>
                  <div className="info-label">CPF</div>
                  <div className="info-value">{formatCpf(driver.driverCpf)}</div>
                </div>
              </div>
            </Grid.Column>
            <Grid.Column>
              <div className="info-item">
                <Icon name="truck" />
                <div>
                  <div className="info-label">Placa</div>
                  <div className="info-value">{driver.plate}</div>
                </div>
              </div>
            </Grid.Column>
            <Grid.Column>
              <div className="info-item">
                <Icon name="warehouse" />
                <div>
                  <div className="info-label">Frota</div>
                  <div className="info-value">{driver.fleetName}</div>
                </div>
              </div>
            </Grid.Column>
            <Grid.Column>
              <div className="info-item">
                <Icon name="road" />
                <div>
                  <div className="info-label">KM Rodados</div>
                  <div className="info-value">{formatNumber(driver.odometro)} km</div>
                </div>
              </div>
            </Grid.Column>
          </Grid>
        </Segment>

        {/* Menu de navegação sticky */}
        <div className="section-nav">
          <div className="section-nav-item" onClick={() => scrollToSection('overview')}>
            <Icon name="dashboard" />
            Visão Geral
          </div>
          <div className="section-nav-item" onClick={() => scrollToSection('performance')}>
            <Icon name="line graph" />
            Performance
          </div>
          <div className="section-nav-item" onClick={() => scrollToSection('prizes')}>
            <Icon name="trophy" />
            Premiações
          </div>
        </div>

        {/* Seção 1: Visão Geral */}
        <div id="overview" className="modal-section">
          <Divider horizontal>
            <Header as="h4">
              <Icon name="chart line" />
              Performance - {periodLabel}
            </Header>
          </Divider>

          {/* Métricas de Performance */}
          <Grid columns={2} stackable divided>
            <Grid.Column>
              <Segment className="metric-card inertia-card">
                <div className="metric-header">
                  <Icon name="tachometer alternate" size="large" />
                  <Header as="h3">Inércia</Header>
                </div>
                <Statistic size="huge">
                  <Statistic.Value>{driver.inertiaPercentage.toFixed(1)}%</Statistic.Value>
                  <Statistic.Label className={`performance-${inertiaLevel.color}`}>
                    {inertiaLevel.level}
                  </Statistic.Label>
                </Statistic>
                
                {clientAverage && (
                  <div className="comparison">
                    <div className="comparison-label">Média da frota: {clientAverage.inertia.toFixed(1)}%</div>
                    <div className={`comparison-value ${driver.inertiaPercentage > clientAverage.inertia ? 'positive' : 'negative'}`}>
                      {driver.inertiaPercentage > clientAverage.inertia ? (
                        <>
                          <Icon name="arrow up" />
                          {(driver.inertiaPercentage - clientAverage.inertia).toFixed(1)}% acima
                        </>
                      ) : (
                        <>
                          <Icon name="arrow down" />
                          {(clientAverage.inertia - driver.inertiaPercentage).toFixed(1)}% abaixo
                        </>
                      )}
                    </div>
                  </div>
                )}

                <Divider />
                
                <div className="prize-info">
                  <Icon name="trophy" color="yellow" />
                  <span className="prize-label">Prêmio Inércia:</span>
                  <span className="prize-value">{formatCurrency(driver.inertiaPrize)}</span>
                </div>
              </Segment>
            </Grid.Column>

            <Grid.Column>
              <Segment className="metric-card motor-card">
                <div className="metric-header">
                  <Icon name="cog" size="large" />
                  <Header as="h3">Carga do Motor</Header>
                </div>
                <Statistic size="huge">
                  <Statistic.Value>{driver.motorLoadPercentage.toFixed(1)}%</Statistic.Value>
                  <Statistic.Label className={`performance-${motorLoadLevel.color}`}>
                    {motorLoadLevel.level}
                  </Statistic.Label>
                </Statistic>
                
                {clientAverage && (
                  <div className="comparison">
                    <div className="comparison-label">Média da frota: {clientAverage.motorLoad.toFixed(1)}%</div>
                    <div className={`comparison-value ${driver.motorLoadPercentage < clientAverage.motorLoad ? 'positive' : 'negative'}`}>
                      {driver.motorLoadPercentage < clientAverage.motorLoad ? (
                        <>
                          <Icon name="arrow down" />
                          {(clientAverage.motorLoad - driver.motorLoadPercentage).toFixed(1)}% abaixo (melhor)
                        </>
                      ) : (
                        <>
                          <Icon name="arrow up" />
                          {(driver.motorLoadPercentage - clientAverage.motorLoad).toFixed(1)}% acima
                        </>
                      )}
                    </div>
                  </div>
                )}

                <Divider />
                
                <div className="prize-info">
                  <Icon name="trophy" color="yellow" />
                  <span className="prize-label">Prêmio Carga Motor:</span>
                  <span className="prize-value">{formatCurrency(driver.engineLoadPrize)}</span>
                </div>
              </Segment>
            </Grid.Column>
          </Grid>

          {/* Total de Prêmios */}
          <Segment className="total-prize-segment" color="green">
            <Grid columns={2} verticalAlign="middle">
              <Grid.Column>
                <Header as="h3">
                  <Icon name="gift" />
                  <Header.Content>
                    Prêmio Total do Período
                    <Header.Subheader>{periodLabel}</Header.Subheader>
                  </Header.Content>
                </Header>
              </Grid.Column>
              <Grid.Column textAlign="right">
                <Statistic size="small" color="green">
                  <Statistic.Value>{formatCurrency(totalPrize)}</Statistic.Value>
                </Statistic>
              </Grid.Column>
            </Grid>
          </Segment>

          {/* Informações Adicionais */}
          <Segment className="additional-info">
            <Header as="h4">
              <Icon name="info circle" />
              Dicas para Melhoria
            </Header>
            <div className="tips-container">
              {driver.inertiaPercentage < 30 && (
                <div className="tip-item">
                  <Icon name="lightbulb" color="yellow" />
                  <p>
                    <strong>Inércia (Meta: próximo de 48%):</strong> Mantenha uma condução mais suave, 
                    evitando acelerações e frenagens bruscas. Utilize o freio motor sempre que possível 
                    e antecipe as paradas para maximizar o uso da inércia.
                  </p>
                </div>
              )}
              {driver.motorLoadPercentage > 3.0 && (
                <div className="tip-item">
                  <Icon name="lightbulb" color="orange" />
                  <p>
                    <strong>Carga do Motor (Meta: abaixo de 3%):</strong> Sua carga está muito alta! 
                    Evite rotações elevadas, utilize marchas mais altas quando possível, mantenha 
                    velocidade constante e planeje as rotas para reduzir ao máximo o esforço do motor.
                    {driver.motorLoadPercentage >= 3.9 && (
                      <span style={{ color: '#dc2626', fontWeight: 700 }}>
                        {' '}Acima de 3.9% você não recebe premiação!
                      </span>
                    )}
                  </p>
                </div>
              )}
              {driver.inertiaPercentage >= 40 && driver.motorLoadPercentage <= 2.5 && (
                <div className="tip-item success">
                  <Icon name="check circle" color="green" />
                  <p>
                    <strong>Parabéns!</strong> Sua performance está excelente! Inércia alta e 
                    carga do motor baixa indicam uma condução muito eficiente. Continue assim!
                  </p>
                </div>
              )}
            </div>
          </Segment>
        </div>

        {/* Seção 2: Gráfico de Performance */}
        <div id="performance" className="modal-section">
          <Header as="h3">
            <Icon name="chart area" />
            <Header.Content>
              Evolução de Performance
              <Header.Subheader>Histórico diário — {periodLabel}</Header.Subheader>
            </Header.Content>
          </Header>
          <DriverPerformanceChart 
            driverCpf={driver.driverCpf} 
            intervalStart={intervalStart}
            intervalEnd={intervalEnd}
          />
        </div>

        {/* Seção 3: Premiações do Período */}
        <div id="prizes" className="modal-section">
          <Header as="h3">
            <Icon name="trophy" />
            <Header.Content>
              Premiações do Período
              <Header.Subheader>Resumo das premiações — {periodLabel}</Header.Subheader>
            </Header.Content>
          </Header>
          
          {loadingMonthData ? (
            <Segment>
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <Icon name="spinner" loading size="big" />
                <p>Carregando dados de premiação...</p>
              </div>
            </Segment>
          ) : monthPerformance ? (
            <>
              <Grid columns={2} stackable className="prizes-summary">
                <Grid.Column width={10} verticalAlign="middle">
                  <Grid columns={3} stackable textAlign="center" verticalAlign="middle">
                    <Grid.Column>
                      <Statistic size="small" color="green">
                        <Statistic.Value>
                          {formatCurrency((monthPerformance.inertiaPrize || 0) + (monthPerformance.engineLoadPrize || 0))}
                        </Statistic.Value>
                        <Statistic.Label>
                          <Icon name="trophy" />
                          Total Período
                        </Statistic.Label>
                      </Statistic>
                    </Grid.Column>
                    <Grid.Column>
                      <Statistic size="small" color="teal">
                        <Statistic.Value>{formatCurrency(monthPerformance.inertiaPrize || 0)}</Statistic.Value>
                        <Statistic.Label>
                          <Icon name="arrow up" />
                          Inércia
                        </Statistic.Label>
                      </Statistic>
                    </Grid.Column>
                    <Grid.Column>
                      <Statistic size="small" color="blue">
                        <Statistic.Value>{formatCurrency(monthPerformance.engineLoadPrize || 0)}</Statistic.Value>
                        <Statistic.Label>
                          <Icon name="cog" />
                          Carga Motor
                        </Statistic.Label>
                      </Statistic>
                    </Grid.Column>
                  </Grid>
                </Grid.Column>
                <Grid.Column width={6} verticalAlign="middle">
                  {pieData && (
                    <div className="prizes-pie-chart">
                      <Doughnut data={pieData} options={pieOptions} />
                    </div>
                  )}
                </Grid.Column>
              </Grid>
              
              {/* Tabela de Dados Diários */}
              <Segment className="daily-data-table-segment">
                <Header as="h4">
                  <Icon name="calendar alternate" />
                  Detalhamento Diário
                  <Header.Subheader>
                    Performance diária — {periodLabel}
                  </Header.Subheader>
                </Header>
                {loadingDailyData ? (
                  <div style={{ textAlign: 'center', padding: '40px' }}>
                    <Icon name="spinner" loading size="big" />
                    <p>Carregando dados diários...</p>
                  </div>
                ) : dailyData && dailyData.length > 0 ? (
                  <div className="table-wrapper">
                    <Table celled striped compact className="daily-performance-table">
                      <Table.Header>
                        <Table.Row>
                          <Table.HeaderCell>Data</Table.HeaderCell>
                          <Table.HeaderCell>Placa</Table.HeaderCell>
                          <Table.HeaderCell textAlign="center">KM</Table.HeaderCell>
                          <Table.HeaderCell textAlign="center">Inércia %</Table.HeaderCell>
                          <Table.HeaderCell textAlign="center">Carga %</Table.HeaderCell>
                          <Table.HeaderCell textAlign="center">Pacotes</Table.HeaderCell>
                        </Table.Row>
                      </Table.Header>
                      <Table.Body>
                        {dailyData.map((day, index) => {
                          const hasInertiaIssue = day.inertiaPercentage < 30;
                          const hasLoadIssue = day.motorLoadPercentage > 3.0;
                          
                          return (
                            <Table.Row key={day.date + index}>
                              <Table.Cell>
                                <strong>{new Date(day.date).toLocaleDateString('pt-BR', { timeZone: 'UTC' })}</strong>
                              </Table.Cell>
                              <Table.Cell>{day.plate}</Table.Cell>
                              <Table.Cell textAlign="center">
                                {formatNumber(day.odometro)}
                              </Table.Cell>
                              <Table.Cell textAlign="center" className={hasInertiaIssue ? 'warning-cell' : 'success-cell'}>
                                {day.inertiaPercentage.toFixed(1)}%
                                {hasInertiaIssue && <Icon name="warning sign" color="orange" style={{ marginLeft: '5px' }} />}
                              </Table.Cell>
                              <Table.Cell textAlign="center" className={hasLoadIssue ? 'warning-cell' : 'success-cell'}>
                                {day.motorLoadPercentage.toFixed(1)}%
                                {hasLoadIssue && <Icon name="warning sign" color="red" style={{ marginLeft: '5px' }} />}
                              </Table.Cell>
                              <Table.Cell textAlign="center">
                                {day.totalPackages}
                              </Table.Cell>
                            </Table.Row>
                          );
                        })}
                      </Table.Body>
                    </Table>
                  </div>
                ) : (
                  <Message info>
                    <Message.Header>Sem dados diários</Message.Header>
                    <p>Não há dados de performance diária para o período selecionado.</p>
                  </Message>
                )}
              </Segment>
              
              <Segment className="prizes-info">
                <Header as="h4">
                  <Icon name="info circle" />
                  Sobre as Premiações
                </Header>
                <p>
                  As premiações são calculadas com base na performance média do período, 
                  considerando os percentuais de inércia e carga do motor. Os valores são 
                  calculados de acordo com as configurações de bônus da frota.
                </p>
              </Segment>
            </>
          ) : (
            <Segment>
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <Icon name="info circle" size="big" />
                <p>Não há dados de premiação disponíveis para o período selecionado.</p>
              </div>
            </Segment>
          )}
        </div>

        {/* Seção 4: Programações do motorista */}
        <div id="programmings" className="modal-section">
          <Header as="h3">
            <Icon name="calendar alternate outline" />
            <Header.Content>
              Programações do motorista
              <Header.Subheader>Viagens no período do dashboard ({periodLabel})</Header.Subheader>
            </Header.Content>
          </Header>
          <Segment>
            <p>
              Consulte todas as programações deste motorista no intervalo selecionado no dashboard,
              com filtros por data, origem e destino.
            </p>
            <Button color="teal" icon labelPosition="left" onClick={() => setProgrammingsModalOpen(true)}>
              <Icon name="list" />
              Ver programações
            </Button>
          </Segment>
        </div>
      </Modal.Content>

      {driver && (
        <DriverProgrammingsModal
          open={programmingsModalOpen}
          onClose={() => setProgrammingsModalOpen(false)}
          driverId={driver.driverId}
          driverName={driver.driverName}
          defaultStartDate={intervalStart}
          defaultEndDate={intervalEnd}
        />
      )}

      <Modal.Actions>
        <Button onClick={onClose} primary>
          <Icon name="checkmark" /> Entendi
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default DriverDetailModal;
